import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { endpoints } from "../../api/endpoints";
import type { Page } from "../../types/user";

export interface Upload {
  id: number;
  fileName: string;
  fileURL: string;
  fileKey: string;
  mediaType: string;
  createdAt: string;
}

export interface UploadQuery {
  pageNumber: number;
  pageSize: number;
  sort: "ASC" | "DESC";
  sortOn: string;
  search: string;
}

export const DEFAULT_UPLOAD_QUERY: UploadQuery = {
  pageNumber: 0,
  pageSize: 25,
  sort: "DESC",
  sortOn: "id",
  search: "",
};

export function useUploads(query: UploadQuery) {
  return useQuery({
    queryKey: ["uploads", query],
    queryFn: async () => {
      const { data } = await api.get<Page<Upload>>(endpoints.uploads.list(), { params: query });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

function useInvalidate() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: ["uploads"] });
}

export function useUploadFile() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ file, mediaType }: { file: File; mediaType?: string }) => {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(endpoints.uploads.create(), form, {
        headers: { "Content-Type": undefined },
        params: mediaType ? { mediaType } : undefined,
      });
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteUpload() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(endpoints.uploads.remove(String(id)));
      return data;
    },
    onSuccess: invalidate,
  });
}
